import { createPortal } from 'react-dom'
import { Icon } from '../components/Icon.js'
import { Button } from '../components/ui.js'
import { cn } from '../lib/cn.js'
import { releaseDate } from './releases.js'
import type { ReleaseNote } from './types.js'

/**
 * The corner card that says a new version brought changes. Decision 115.
 *
 * A card and not a window, because it arrives while somebody is mid-booking:
 * it waits beside the work until they choose to read it or put it away.
 * "Later" puts it away until the next load; the cross marks it read, and the
 * same notes stay on the What’s new page.
 */
export function WhatsNewToast({
  release,
  changes,
  field,
  onOpen,
  onLater,
  onDismiss,
}: {
  release: ReleaseNote
  changes: number
  field: boolean
  onOpen: () => void
  /** Out of the way for now; it comes back on the next load. */
  onLater: () => void
  /** Read: it does not come back. */
  onDismiss: () => void
}) {
  return createPortal(
    <aside
      aria-label="What’s new"
      data-guide="whats-new-toast"
      className={cn(
        'fixed z-[45] flex animate-[guide-rise_320ms_ease-out] overflow-hidden rounded-lg bg-white shadow-[0_18px_44px_rgb(15_23_42_/_0.22),0_4px_12px_rgb(15_23_42_/_0.1)] ring-1 ring-ink-200 motion-reduce:animate-none',
        field
          ? 'inset-x-3 bottom-[calc(4.75rem+var(--safe-bottom))]'
          : 'bottom-4 left-[calc(var(--sidebar-width)+16px)] w-[360px]',
      )}
    >
      <span aria-hidden="true" className="w-1 shrink-0 bg-brand-500" />
      <div className="min-w-0 flex-1 py-3 pr-2 pl-3.5">
        <p className="flex items-center gap-1.5 text-[10.5px] font-bold tracking-wider text-brand-700 uppercase">
          <Icon name="news" className="size-3.5" strokeWidth={2} />
          What’s new · {releaseDate(release.id)}
        </p>
        <p className="mt-1 text-sm leading-snug font-semibold text-ink-900">{release.title}</p>
        <p className="mt-0.5 text-xs text-ink-500">
          {changes === 1 ? 'One change' : `${changes} changes`} for you
        </p>
        <div className="mt-2.5 flex items-center gap-1.5">
          <Button size="sm" variant="primary" onClick={onOpen}>
            See what’s new
          </Button>
          <Button size="sm" variant="ghost" onClick={onLater}>
            Later
          </Button>
        </div>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss what’s new"
        className="m-1.5 grid size-7 shrink-0 place-items-center self-start rounded-md text-ink-400 transition-colors hover:bg-ink-100 hover:text-ink-900"
      >
        <Icon name="close" className="size-4" />
      </button>
    </aside>,
    document.body,
  )
}
